/**
 * The per-Aufgabe Antwortentwurf (S3).
 *
 * GET /tasks/:id/draft generates on first read from the comm assist, cached when present,
 * so reading it as the selection changes is cheap. 404 task_draft_unavailable means the
 * task has no mail or the assist refused it - the caller says so rather than showing an
 * empty editor. PUT saves an edit; the original AI text is kept server-side.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaskDraftView } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';

export function useTaskDraft(taskId: string | null) {
  const live = isLiveData();
  return useQuery<TaskDraftView | null>({
    queryKey: qk.taskDraft(taskId ?? 'none'),
    queryFn: () => (taskId === null ? null : apiClient.tasks.draft(taskId)),
    enabled: live && taskId !== null,
    retry: false,
  });
}

export function useSaveTaskDraft(taskId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftText: string) => {
      if (taskId === null) return Promise.reject(new Error('kein Task'));
      return apiClient.tasks.putDraft(taskId, { draft_text: draftText });
    },
    onSuccess: (saved) => {
      qc.setQueryData(qk.taskDraft(taskId ?? 'none'), saved);
    },
  });
}
